import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Plus, ClipboardList, Pencil, Copy, Trash2, Check, X } from 'lucide-react'
import api from '../../../core/services/api'
import { Toast } from '../../../components/Toast'
import ConfirmModal from '../../../components/ConfirmModal'
import type { SchedaTemplateList } from '../../../types/schede'
import { ENTITY_TYPE_LABELS } from '../../../types/schede'

export default function SchedeDinamichePage() {
  const navigate = useNavigate()
  const [templates, setTemplates] = useState<SchedaTemplateList[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterCategoria, setFilterCategoria] = useState('')
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [deleteId, setDeleteId] = useState<string | null>(null)

  // Create modal state
  const [showCreate, setShowCreate] = useState(false)
  const [createNome, setCreateNome] = useState('')
  const [createDescrizione, setCreateDescrizione] = useState('')
  const [createCategoria, setCreateCategoria] = useState('')
  const [createEntityTypes, setCreateEntityTypes] = useState<string[]>(['fase_commessa'])
  const [createRiepilogabile, setCreateRiepilogabile] = useState(true)
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    loadTemplates()
  }, [search, filterCategoria])

  const loadTemplates = async () => {
    try {
      const params: any = {}
      if (search) params.search = search
      if (filterCategoria) params.categoria = filterCategoria
      const res = await api.get('/v1/schede-dinamiche/templates', { params })
      setTemplates(res.data)
    } catch (err) {
      setToast({ message: 'Errore caricamento template', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = async () => {
    if (!createNome.trim()) return
    setCreating(true)
    try {
      const res = await api.post('/v1/schede-dinamiche/templates', {
        nome: createNome.trim(),
        descrizione: createDescrizione.trim() || null,
        categoria: createCategoria.trim() || null,
        entity_types: createEntityTypes,
        is_riepilogabile: createRiepilogabile,
      })
      setShowCreate(false)
      setCreateNome('')
      setCreateDescrizione('')
      setCreateCategoria('')
      setCreateEntityTypes(['fase_commessa'])
      setCreateRiepilogabile(true)
      navigate(`/app/configurazione/schede-dinamiche/${res.data.id}/builder`)
    } catch (err: any) {
      setToast({ message: err.response?.data?.detail || 'Errore creazione', type: 'error' })
    } finally {
      setCreating(false)
    }
  }

  const handleDuplica = async (id: string) => {
    try {
      const res = await api.post(`/v1/schede-dinamiche/templates/${id}/duplica`)
      setToast({ message: `Template duplicato: ${res.data.codice}`, type: 'success' })
      loadTemplates()
    } catch (err) {
      setToast({ message: 'Errore duplicazione', type: 'error' })
    }
  }

  const confirmDelete = async () => {
    if (!deleteId) return
    try {
      await api.delete(`/v1/schede-dinamiche/templates/${deleteId}`)
      setToast({ message: 'Template disattivato', type: 'success' })
      loadTemplates()
    } catch (err) {
      setToast({ message: 'Errore eliminazione', type: 'error' })
    } finally {
      setDeleteId(null)
    }
  }

  const toggleEntityType = (type: string) => {
    setCreateEntityTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    )
  }

  // Categorie uniche dai template
  const categorie = [...new Set(templates.map(t => t.categoria).filter(Boolean))] as string[]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ClipboardList className="w-7 h-7" />
            Schede Dinamiche
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
            Configura form personalizzati per raccolta dati
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors"
          style={{ backgroundColor: 'var(--color-primary)' }}
        >
          <Plus className="w-4 h-4" />
          Nuovo Template
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-tertiary)' }} />
          <input
            type="text"
            placeholder="Cerca template..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg text-sm border"
            style={{
              backgroundColor: 'var(--color-bg-input, var(--color-bg-card))',
              borderColor: 'var(--color-border)',
              color: 'var(--color-text-primary)',
            }}
          />
        </div>
        {categorie.length > 0 && (
          <select
            value={filterCategoria}
            onChange={(e) => setFilterCategoria(e.target.value)}
            className="px-3 py-2 rounded-lg text-sm border"
            style={{
              backgroundColor: 'var(--color-bg-input, var(--color-bg-card))',
              borderColor: 'var(--color-border)',
              color: 'var(--color-text-primary)',
            }}
          >
            <option value="">Tutte le categorie</option>
            {categorie.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
      </div>

      {/* Template List */}
      {loading ? (
        <div className="text-center py-12" style={{ color: 'var(--color-text-tertiary)' }}>
          Caricamento...
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center py-12 rounded-lg border" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-card)' }}>
          <ClipboardList className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--color-text-tertiary)' }} />
          <p style={{ color: 'var(--color-text-secondary)' }}>Nessun template trovato</p>
          <p className="text-sm mt-1" style={{ color: 'var(--color-text-tertiary)' }}>
            Crea il primo template per iniziare a costruire schede personalizzate
          </p>
        </div>
      ) : (
        <div className="grid gap-4 grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
          {templates.map(tpl => (
            <div
              key={tpl.id}
              className="rounded-lg border p-4 transition-all hover:shadow-md"
              style={{
                borderColor: 'var(--color-border)',
                backgroundColor: 'var(--color-bg-card)',
              }}
            >
              {/* Card header */}
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold truncate">{tpl.nome}</h3>
                  <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>{tpl.codice}</p>
                </div>
                <div className="flex items-center gap-1 ml-2">
                  {tpl.is_published ? (
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-green-100 text-green-700">
                      v{tpl.versione - 1}
                    </span>
                  ) : (
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-yellow-100 text-yellow-700">
                      Bozza
                    </span>
                  )}
                </div>
              </div>

              {/* Description */}
              {tpl.descrizione && (
                <p className="text-sm mb-3 line-clamp-2" style={{ color: 'var(--color-text-secondary)' }}>
                  {tpl.descrizione}
                </p>
              )}

              {/* Meta */}
              <div className="flex flex-wrap gap-1 mb-3">
                {(tpl.entity_types || []).map(et => (
                  <span
                    key={et}
                    className="text-[10px] px-2 py-0.5 rounded-full"
                    style={{
                      backgroundColor: 'var(--color-bg-surface, rgba(99,102,241,0.1))',
                      color: 'var(--color-primary)',
                    }}
                  >
                    {ENTITY_TYPE_LABELS[et] || et}
                  </span>
                ))}
                {tpl.categoria && (
                  <span
                    className="text-[10px] px-2 py-0.5 rounded-full"
                    style={{
                      backgroundColor: 'var(--color-bg-surface, rgba(100,100,100,0.1))',
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    {tpl.categoria}
                  </span>
                )}
              </div>

              {/* Stats */}
              <div className="flex items-center gap-4 text-xs mb-3" style={{ color: 'var(--color-text-tertiary)' }}>
                <span>{tpl.fields_count} campi</span>
                <span>{tpl.istanze_count} istanze</span>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 border-t pt-3" style={{ borderColor: 'var(--color-border)' }}>
                <button
                  onClick={() => navigate(`/app/configurazione/schede-dinamiche/${tpl.id}/builder`)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors"
                  style={{ backgroundColor: 'var(--color-primary)', color: '#fff' }}
                >
                  <Pencil className="w-3 h-3" />
                  Editor
                </button>
                <button
                  onClick={() => handleDuplica(tpl.id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs transition-colors hover:opacity-80"
                  style={{ color: 'var(--color-text-secondary)' }}
                  title="Duplica"
                >
                  <Copy className="w-3 h-3" />
                </button>
                <button
                  onClick={() => setDeleteId(tpl.id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs transition-colors hover:opacity-80"
                  style={{ color: 'var(--color-danger, #ef4444)' }}
                  title="Disattiva"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div
            className="w-full max-w-lg rounded-xl p-6 shadow-2xl"
            style={{ backgroundColor: 'var(--color-bg-card)', color: 'var(--color-text-primary)' }}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Nuovo Template</h2>
              <button onClick={() => setShowCreate(false)}>
                <X className="w-5 h-5" style={{ color: 'var(--color-text-tertiary)' }} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium block mb-1">Nome *</label>
                <input
                  type="text"
                  value={createNome}
                  onChange={(e) => setCreateNome(e.target.value)}
                  placeholder="es. Scheda Manutenzione CNC"
                  className="w-full px-3 py-2 rounded-lg text-sm border"
                  style={{
                    backgroundColor: 'var(--color-bg-input, var(--color-bg-card))',
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-primary)',
                  }}
                  autoFocus
                />
              </div>

              <div>
                <label className="text-sm font-medium block mb-1">Descrizione</label>
                <textarea
                  value={createDescrizione}
                  onChange={(e) => setCreateDescrizione(e.target.value)}
                  placeholder="Descrizione opzionale..."
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg text-sm border resize-none"
                  style={{
                    backgroundColor: 'var(--color-bg-input, var(--color-bg-card))',
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-primary)',
                  }}
                />
              </div>

              <div>
                <label className="text-sm font-medium block mb-1">Categoria</label>
                <input
                  type="text"
                  value={createCategoria}
                  onChange={(e) => setCreateCategoria(e.target.value)}
                  placeholder="es. Manutenzione, Qualita', Audit..."
                  className="w-full px-3 py-2 rounded-lg text-sm border"
                  style={{
                    backgroundColor: 'var(--color-bg-input, var(--color-bg-card))',
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-primary)',
                  }}
                />
              </div>

              <div>
                <label className="text-sm font-medium block mb-2">Collegabile a</label>
                <div className="flex gap-2 flex-wrap">
                  {Object.entries(ENTITY_TYPE_LABELS).map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => toggleEntityType(key)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors"
                      style={{
                        borderColor: createEntityTypes.includes(key) ? 'var(--color-primary)' : 'var(--color-border)',
                        backgroundColor: createEntityTypes.includes(key) ? 'var(--color-primary)' : 'transparent',
                        color: createEntityTypes.includes(key) ? '#fff' : 'var(--color-text-secondary)',
                      }}
                    >
                      {createEntityTypes.includes(key) && <Check className="w-3 h-3" />}
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={createRiepilogabile}
                    onChange={(e) => setCreateRiepilogabile(e.target.checked)}
                    className="rounded text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium">Soggetta a riepilogo</span>
                  <span className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
                    (le istanze possono essere aggregate in riepiloghi)
                  </span>
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowCreate(false)}
                className="px-4 py-2 rounded-lg text-sm"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                Annulla
              </button>
              <button
                onClick={handleCreate}
                disabled={!createNome.trim() || createEntityTypes.length === 0 || creating}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-40"
                style={{ backgroundColor: 'var(--color-primary)' }}
              >
                {creating ? 'Creazione...' : 'Crea e apri Builder'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!deleteId}
        title="Disattivare template"
        message="Disattivare questo template? Le istanze esistenti non saranno modificate."
        confirmLabel="Disattiva"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteId(null)}
      />

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  )
}
